import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import {
  Clock, Flag, Route, ChevronRight, Trophy, Info, AlertTriangle, ArrowRight
} from 'lucide-react';

const boucleConfig = {
  ROUGE:  { hex: '#dc2626', label: 'Boucle 10 km',         distance: '9,55 km',  short: 'R' },
  VERT:   { hex: '#16a34a', label: 'Boucle 20 km Sanxay',  distance: '19,81 km', short: 'V' },
  VERTE:  { hex: '#16a34a', label: 'Boucle 20 km Sanxay',  distance: '19,81 km', short: 'V' },
  BLEU:   { hex: '#2563eb', label: 'Boucle 20 km Rouillé', distance: '21,30 km', short: 'B' },
  NOIR:   { hex: '#18181b', label: 'Boucle 30 km Sanxay',  distance: '30,08 km', short: 'N' },
  ORANGE: { hex: '#ea580c', label: 'Boucle 30 km Rouillé', distance: '29,23 km', short: 'O' },
};

const departures = [
  {
    distance: '10 km', total: '9,55 km', heure: '11h00',
    categories: ['Club 4', 'Club Poney 4', 'Club A'],
    boucles: ['ROUGE'],
  },
  {
    distance: '20 km', total: '19,81 km', heure: '10h30',
    categories: ['Club 3', 'Club 2', 'Amateur 4', 'Prépa 20', 'SHF 4, 5 & 6 ans VI'],
    boucles: ['VERTE'],
  },
  {
    distance: '40 km', total: '41,11 km', heure: '10h00',
    categories: ['Prépa 40 VR', 'Amateur 3 VR', 'Club 1 VR', 'SHF 5 ans VR'],
    boucles: ['VERT', 'BLEU'],
  },
  {
    distance: '40 km', total: '41,11 km', heure: '09h30',
    categories: ['Prépa 40', 'Amateur 3', 'Club 1', 'SHF 5 & 6 ans VI'],
    boucles: ['VERT', 'BLEU'],
  },
  {
    distance: '60 km', total: '59,31 km', heure: '09h00',
    categories: ['Prépa 60 VR', 'Club Elite VR', 'Amateur 2 VR', 'SHF 5 & 6 ans VR'],
    boucles: ['NOIR', 'ORANGE'],
  },
  {
    distance: '60 km', total: '59,31 km', heure: '08h30',
    categories: ['Prépa 60', 'Club Elite', 'Amateur 2', 'SHF 5 & 6 ans VI'],
    boucles: ['NOIR', 'ORANGE'],
  },
  {
    distance: '80 km', total: '80,61 km', heure: '08h00',
    categories: ['Prépa 80 VR', 'Amateur 1 VL', 'Amateur 1 VR', 'SHF 6 ans VR'],
    boucles: ['NOIR', 'ORANGE', 'VERT'],
  },
  {
    distance: '80 km', total: '80,61 km', heure: '07h30',
    categories: ['Prépa 80', 'Amateur 1', 'SHF 5 & 6 ans VI'],
    boucles: ['NOIR', 'ORANGE', 'VERT'],
  },
  {
    distance: '100 km', total: '100,42 km', heure: '07h00',
    categories: ['Amateur 1 GP VL'],
    boucles: ['NOIR', 'ORANGE', 'BLEU', 'VERT'],
  },
  {
    distance: '120 km', total: '121,72 km', heure: '06h30',
    categories: ['Amateur Elite VL'],
    boucles: ['NOIR', 'ORANGE', 'VERT', 'BLEU', 'VERT'],
  },
];

function BoucleBadge({ name, size = 'md' }) {
  const config = boucleConfig[name];
  if (!config) return null;
  const sizeClasses = size === 'sm'
    ? 'w-6 h-6 text-xs'
    : size === 'lg' ? 'w-10 h-10 text-base' : 'w-8 h-8 text-sm';
  return (
    <div
      className={`${sizeClasses} rounded-full flex items-center justify-center font-bold text-white shadow-md flex-shrink-0`}
      style={{ backgroundColor: config.hex }}
      title={`${config.label} (${config.distance})`}
    >
      {config.short}
    </div>
  );
}

function BoucleSequence({ boucles, showArrows = true }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {boucles.map((b, i) => (
        <React.Fragment key={i}>
          <BoucleBadge name={b} />
          {showArrows && i < boucles.length - 1 && (
            <ArrowRight className="h-3.5 w-3.5 text-zinc-300 flex-shrink-0" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function ProgrammeEndurance() {
  const { t } = useTranslation();
  return (
    <div className="space-y-10" data-testid="programme-endurance">

      {/* ── 1. PROGRAMME DES DÉPARTS ── */}
      <div data-testid="programme-departs">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-fuchsia-100 rounded-xl"><Flag className="h-5 w-5 text-fuchsia-600" /></div>
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900">{t('ip.progDeparts')}</h3>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block">
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="departs-table">
                <thead>
                  <tr className="bg-zinc-800 text-white">
                    <th className="py-4 px-5 text-left font-semibold min-w-[100px]">
                      <div className="flex items-center gap-2"><Route className="h-4 w-4" />{t('ip.progDistance')}</div>
                    </th>
                    <th className="py-4 px-5 text-left font-semibold min-w-[130px]">
                      <div className="flex items-center gap-2"><Clock className="h-4 w-4" />{t('ip.progDepart')}</div>
                    </th>
                    <th className="py-4 px-5 text-left font-semibold">{t('ip.progCategories')}</th>
                    <th className="py-4 px-5 text-left font-semibold min-w-[180px]">{t('ip.progBoucles')}</th>
                  </tr>
                </thead>
                <tbody>
                  {departures.map((dep, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/70'} hover:bg-fuchsia-50/40 transition-colors`} data-testid={`depart-row-${i}`}>
                      <td className="py-4 px-5">
                        <span className="text-xl font-bold text-zinc-900">{dep.distance}</span>
                        <span className="block text-xs text-zinc-400 mt-0.5">{dep.total} {t('ip.progReal')}</span>
                      </td>
                      <td className="py-4 px-5">
                        <span className="text-lg font-bold text-fuchsia-600">{dep.heure}</span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex flex-wrap gap-1.5">
                          {dep.categories.map((cat, j) => (
                            <span key={j} className="inline-block px-2.5 py-1 bg-zinc-100 text-zinc-700 rounded-full text-xs font-medium">{cat}</span>
                          ))}
                        </div>
                        {dep.detail && <p className="text-xs text-zinc-400 mt-2 italic">{dep.detail}</p>}
                      </td>
                      <td className="py-4 px-5">
                        <BoucleSequence boucles={dep.boucles} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-3">
          {departures.map((dep, i) => (
            <Card key={i} className="border-0 shadow-lg overflow-hidden" data-testid={`depart-card-${i}`}>
              <div className="flex">
                <div className="w-2 flex-shrink-0" style={{ backgroundColor: boucleConfig[dep.boucles[0]]?.hex || '#d946ef' }} />
                <CardContent className="p-4 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-2xl font-bold text-zinc-900">{dep.distance}</span>
                      <span className="text-xs text-zinc-400 ml-2">{dep.total}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-fuchsia-600">{dep.heure}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {dep.categories.map((cat, j) => (
                      <span key={j} className="px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded-full text-xs">{cat}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-400 font-medium">{t('ip.progBouclesLabel')} :</span>
                    <BoucleSequence boucles={dep.boucles} />
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* ── 2. COMMENT LIRE VOTRE COURSE ── */}
      <div data-testid="programme-lecture-parcours">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-lime-100 rounded-xl"><Route className="h-5 w-5 text-lime-700" /></div>
          <div>
            <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900">{t('ip.progReadCourse')}</h3>
            <p className="text-zinc-500 text-sm">{t('ip.progReadCourseSub')}</p>
          </div>
        </div>

        {/* Légende des boucles */}
        <Card className="border-0 shadow-lg mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-zinc-800 to-zinc-700 px-5 py-3">
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider">{t('ip.progLegend')}</h4>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="legende-boucles">
              {Object.entries(boucleConfig).filter(([k]) => k !== 'VERTE').map(([name, cfg]) => (
                <div key={name} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50">
                  <BoucleBadge name={name} size="lg" />
                  <div>
                    <span className="font-bold text-zinc-900 text-sm block">{name}</span>
                    <span className="text-xs text-zinc-500">{cfg.label}</span>
                    <span className="text-xs text-zinc-400 block">{cfg.distance}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Schéma d'enchaînement */}
        <Card className="border-0 shadow-xl overflow-hidden">
          <CardContent className="p-5 md:p-8">
            <div className="space-y-4" data-testid="enchainement-parcours">
              {departures.map((dep, i) => (
                <div key={i} className={`flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 p-4 rounded-xl transition-colors ${i % 2 === 0 ? 'bg-zinc-50' : 'bg-white'}`}>
                  <div className="flex items-center gap-3 min-w-[120px]">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: boucleConfig[dep.boucles[0]]?.hex }} />
                    <span className="text-lg font-bold text-zinc-900">{dep.distance}</span>
                  </div>
                  <div className="hidden sm:block text-zinc-300">=</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {dep.boucles.map((b, j) => {
                      const cfg = boucleConfig[b];
                      return (
                        <React.Fragment key={j}>
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow-sm" style={{ backgroundColor: cfg?.hex }}>
                            {b}
                            <span className="opacity-70 font-normal">({cfg?.distance})</span>
                          </div>
                          {j < dep.boucles.length - 1 && (
                            <ArrowRight className="h-4 w-4 text-zinc-300 flex-shrink-0" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── 3. ORGANISATION DE LA JOURNÉE ── */}
      <div data-testid="programme-organisation">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-amber-100 rounded-xl"><Trophy className="h-5 w-5 text-amber-700" /></div>
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900">{t('ip.progOrganisation')}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Remise des prix */}
          <Card className="border-0 shadow-lg overflow-hidden" data-testid="remise-prix">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-5">
              <div className="flex items-center gap-3">
                <Trophy className="h-7 w-7 text-white" />
                <h4 className="text-white font-bold text-lg">{t('ip.progPrizes')}</h4>
              </div>
            </div>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl">
                <div className="text-center min-w-[70px]">
                  <span className="text-2xl font-bold text-amber-700">15h30</span>
                  <span className="block text-xs text-amber-500">– 16h00</span>
                </div>
                <div>
                  <p className="font-semibold text-zinc-900">{t('ip.courses10to80')}</p>
                  <p className="text-sm text-zinc-500 mt-1">Club, Amateur, Prépa, SHF</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="text-center min-w-[70px]">
                  <span className="text-2xl font-bold text-purple-700">20h00</span>
                  <span className="block text-xs text-purple-500">– 20h30</span>
                </div>
                <div>
                  <p className="font-semibold text-zinc-900">{t('ip.courses100and120')}</p>
                  <p className="text-sm text-zinc-500 mt-1">Amateur 1 GP, Amateur Elite</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informations importantes */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-zinc-50 to-white" data-testid="infos-importantes">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <Info className="h-5 w-5 text-fuchsia-600" />
                <h4 className="font-bold text-lg text-zinc-900">{t('ip.progImportantInfo')}</h4>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-fuchsia-500 flex-shrink-0" />
                  <p className="text-sm text-zinc-700">{t('ip.progBriefing')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-fuchsia-500 flex-shrink-0" />
                  <p className="text-sm text-zinc-700">{t('ip.progAdjust')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-fuchsia-500 flex-shrink-0" />
                  <p className="text-sm text-zinc-700">{t('ip.progVet')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-fuchsia-500 flex-shrink-0" />
                  <p className="text-sm text-zinc-700">{t('ip.progReglement')}</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">{t('ip.progWarning')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
